/**
 * =============================================================================
 * IDS CRM Web - Add / Edit Contact Modal
 * =============================================================================
 *
 * PURPOSE:
 * Creates or edits a contact on an organisation's master list, with optional
 * placement at one of its farm sites (customers) or depots (distributors).
 * On create, the server's duplicate detection (409) is surfaced as a choice:
 * link the existing person instead, or create anyway.
 *
 * =============================================================================
 */

import React, { useState } from 'react';
import './ContactModal.css';

const CONTACT_ROLES = [
  { value: '', label: 'Not set' },
  { value: 'owner', label: 'Owner' },
  { value: 'farm_manager', label: 'Farm Manager' },
  { value: 'herd_manager', label: 'Herd Manager' },
  { value: 'worker', label: 'Worker' },
  { value: 'accounts', label: 'Accounts' }
];

const CONTACT_CHANNELS = [
  { value: 'phone', label: 'Phone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'video', label: 'Video' },
  { value: 'in_person', label: 'In person' }
];

function ContactModal({
  authFetch,
  customerId,
  isDistributor,
  sites = [],
  depots = [],
  contact = null,
  defaultPlacementId = '',
  onClose,
  onSaved
}) {
  const isEdit = Boolean(contact);
  const placementOptions = isDistributor
    ? depots.map(d => ({ id: d.depot_id, label: `${d.depot_name}${d.is_head_office ? ' (Head office)' : ''}` }))
    : sites.map(s => ({ id: s.site_id, label: s.site_name }));

  const [formData, setFormData] = useState({
    full_name: contact?.full_name || '',
    job_title: contact?.job_title || '',
    role: contact?.role || '',
    phone: contact?.phone || '',
    email: contact?.email || '',
    preferred_channel: contact?.preferred_channel || 'phone',
    placement_id: contact
      ? (contact.farm_site_id || contact.depot_id || '')
      : (defaultPlacementId || (placementOptions.length === 1 ? placementOptions[0].id : '')),
    is_primary: Boolean(contact?.is_primary),
    is_decision_maker: Boolean(contact?.is_decision_maker),
    notes: contact?.notes || ''
  });
  const [error, setError] = useState(null);
  const [duplicates, setDuplicates] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const buildPayload = (force) => ({
    full_name: formData.full_name.trim(),
    job_title: formData.job_title.trim() || null,
    role: formData.role || null,
    phone: formData.phone.trim() || null,
    email: formData.email.trim() || null,
    preferred_channel: formData.preferred_channel,
    farm_site_id: !isDistributor ? (formData.placement_id || null) : null,
    depot_id: isDistributor ? (formData.placement_id || null) : null,
    is_primary: formData.is_primary,
    is_decision_maker: formData.is_decision_maker,
    notes: formData.notes.trim() || null,
    ...(isEdit
      ? { updated_at: contact.updated_at }
      : { customer_id: customerId, ...(force ? { force: true } : {}) })
  });

  const submit = async (force = false) => {
    if (!formData.full_name.trim()) {
      setError('Full name is required');
      return;
    }
    setSaving(true);
    setError(null);
    setDuplicates(null);
    try {
      const response = await authFetch(
        isEdit ? `/api/v1/contacts/${contact.contact_id}` : '/api/v1/contacts',
        {
          method: isEdit ? 'PUT' : 'POST',
          body: JSON.stringify(buildPayload(force))
        }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        onSaved();
        return;
      }
      if (response.status === 409 && data.duplicates) {
        setDuplicates(data.duplicates);
        return;
      }
      setError(data.message || 'Failed to save contact');
    } catch (err) {
      setError('Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  // Duplicate found on create: link the existing person to this organisation
  // instead of double-entering them.
  const linkExisting = async (duplicate) => {
    setSaving(true);
    setError(null);
    try {
      const response = await authFetch(`/api/v1/contacts/${duplicate.contact_id}/links`, {
        method: 'POST',
        body: JSON.stringify({ customer_id: customerId, link_role: formData.job_title.trim() || null })
      });
      if (response.ok) {
        onSaved();
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.message || 'Failed to link contact');
      }
    } catch (err) {
      setError('Failed to link contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? `Edit ${contact.full_name}` : 'Add Contact'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}

          {duplicates && (
            <div className="contact-duplicate-panel">
              <p><strong>This person may already exist.</strong> Link them to this organisation instead of creating a duplicate:</p>
              {duplicates.map(dup => (
                <div key={dup.contact_id} className="contact-duplicate-row">
                  <span>
                    {dup.full_name} — {dup.customer_name}
                    {dup.email ? ` · ${dup.email}` : ''}{dup.phone ? ` · ${dup.phone}` : ''}
                  </span>
                  <button className="btn-secondary-small" onClick={() => linkExisting(dup)} disabled={saving}>
                    Link instead
                  </button>
                </div>
              ))}
              <button className="btn-link" onClick={() => submit(true)} disabled={saving}>
                It's a different person — create anyway
              </button>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="contact_full_name">Full Name *</label>
            <input id="contact_full_name" name="full_name" value={formData.full_name} onChange={handleChange} autoFocus={!isEdit} />
          </div>
          <div className="form-group">
            <label htmlFor="contact_job_title">Job Title</label>
            <input id="contact_job_title" name="job_title" value={formData.job_title} onChange={handleChange} placeholder="e.g. Herd Manager" />
          </div>
          <div className="form-group">
            <label htmlFor="contact_role">Role</label>
            <select id="contact_role" name="role" value={formData.role} onChange={handleChange}>
              {CONTACT_ROLES.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="contact_phone">Phone</label>
            <input id="contact_phone" name="phone" value={formData.phone} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="contact_email">Email</label>
            <input id="contact_email" name="email" type="email" value={formData.email} onChange={handleChange} />
          </div>
          <div className="form-group">
            <label htmlFor="contact_preferred_channel">Preferred Channel</label>
            <select id="contact_preferred_channel" name="preferred_channel" value={formData.preferred_channel} onChange={handleChange}>
              {CONTACT_CHANNELS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          {placementOptions.length > 0 && (
            <div className="form-group">
              <label htmlFor="contact_placement">{isDistributor ? 'Depot' : 'Site'}</label>
              <select id="contact_placement" name="placement_id" value={formData.placement_id} onChange={handleChange}>
                <option value="">Whole organisation</option>
                {placementOptions.map(option => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="form-group contact-modal-checkboxes">
            <label>
              <input type="checkbox" name="is_primary" checked={formData.is_primary} onChange={handleChange} />
              Primary contact
            </label>
            <label>
              <input type="checkbox" name="is_decision_maker" checked={formData.is_decision_maker} onChange={handleChange} />
              Decision maker
            </label>
          </div>
          <div className="form-group">
            <label htmlFor="contact_notes">Notes</label>
            <textarea id="contact_notes" name="notes" rows={2} value={formData.notes} onChange={handleChange} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={() => submit(false)} disabled={saving}>
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add Contact')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ContactModal;
